import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';

interface CameraInfo {
  name: string;
  position: { x: number; y: number; z: number };
  target?: { x: number; y: number; z: number };
  type: string;
  isMapCamera: boolean;
}

/**
 * Camera Panel
 * 
 * Features:
 * - List all cameras in the scene
 * - Show camera positions
 * - Jump to camera views
 * - Highlight map cameras vs gameplay cameras
 */
@customElement('camera-panel')
export class CameraPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      color: #eee;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
    }

    .panel-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 12px;
      gap: 8px;
      overflow: hidden;
    }

    .section-title {
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      opacity: 0.75;
      letter-spacing: 0.5px;
      margin: 8px 0 4px 0;
    }

    .camera-list {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .camera-item {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 4px;
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .camera-item.map-camera {
      border-color: rgba(76, 175, 80, 0.5);
      background: rgba(76, 175, 80, 0.08);
    }

    .camera-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    .camera-name {
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .camera-badge {
      font-size: 9px;
      padding: 2px 6px;
      border-radius: 3px;
      background: rgba(255, 255, 255, 0.15);
      text-transform: uppercase;
      font-weight: 600;
    }

    .camera-badge.map {
      background: rgba(76, 175, 80, 0.3);
      color: #4CAF50;
    }

    .camera-badge.gameplay {
      background: rgba(33, 150, 243, 0.3);
      color: #2196F3;
    }

    .camera-info {
      font-size: 11px;
      opacity: 0.7;
      font-family: ui-monospace, SFMono-Regular, monospace;
    }

    .camera-position {
      display: grid;
      grid-template-columns: 20px 1fr;
      gap: 4px;
      font-size: 10px;
      opacity: 0.7;
      margin-top: 4px;
    }

    .coord-label {
      font-weight: 600;
      opacity: 0.5;
    }

    button {
      padding: 4px 10px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      background: rgba(255, 255, 255, 0.08);
      color: #eee;
      cursor: pointer;
      font-size: 11px;
      white-space: nowrap;
    }

    button:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    button:active {
      background: rgba(255, 255, 255, 0.22);
    }

    .controls {
      display: flex;
      gap: 8px;
      padding-top: 4px;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      margin-top: 4px;
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
      opacity: 0.5;
      font-size: 11px;
    }

    .checkbox-row {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 0;
    }

    input[type="checkbox"] {
      cursor: pointer;
    }

    label {
      cursor: pointer;
      user-select: none;
    }
  `;

  @state()
  private cameras: CameraInfo[] = [];

  @state()
  private showDebugCameras = true;

  private eventListener?: (e: Event) => void;

  connectedCallback() {
    super.connectedCallback();
    
    // Subscribe to camera list updates
    this.eventListener = (e: Event) => {
      const ce = e as CustomEvent;
      const event = ce.detail;
      
      if (event?.type === 'cameras:list') {
        this.cameras = event.cameras;
      }
    };
    
    window.addEventListener(DebugUiBridge.uiEventName, this.eventListener);
    this.refreshCameras();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.eventListener) {
      window.removeEventListener(DebugUiBridge.uiEventName, this.eventListener);
    }
  }

  private refreshCameras() {
    DebugUiBridge.dispatchCommand({ type: 'cameras:get' });
  }

  private jumpToCamera(cameraName: string) {
    DebugUiBridge.dispatchCommand({ type: 'camera:jumpTo', cameraName });
  }

  private toggleCameraDebugVisibility() {
    this.showDebugCameras = !this.showDebugCameras;
    DebugUiBridge.dispatchCommand({ type: 'cameras:debugVisibility:set', visible: this.showDebugCameras });
  }

  render() {
    const mapCameras = this.cameras.filter(c => c.isMapCamera);
    const gameplayCameras = this.cameras.filter(c => !c.isMapCamera);

    return html`
      <div class="panel-content">
        <div class="checkbox-row">
          <input 
            type="checkbox" 
            id="show-cameras"
            ?checked=${this.showDebugCameras}
            @change=${this.toggleCameraDebugVisibility}
          />
          <label for="show-cameras">Show Camera Indicators</label>
        </div>

        <div class="camera-list">
          ${mapCameras.length > 0 ? html`
            <div class="section-title">Map Cameras (${mapCameras.length})</div>
            ${mapCameras.map(camera => this.renderCameraItem(camera))}
          ` : ''}

          ${gameplayCameras.length > 0 ? html`
            <div class="section-title">Gameplay Cameras (${gameplayCameras.length})</div>
            ${gameplayCameras.map(camera => this.renderCameraItem(camera))}
          ` : ''}

          ${this.cameras.length === 0 ? html`
            <div class="empty-state">No cameras found in scene</div>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderCameraItem(camera: CameraInfo) {
    return html`
      <div class="camera-item ${camera.isMapCamera ? 'map-camera' : ''}">
        <div class="camera-header">
          <div class="camera-name">
            <span>${camera.name}</span>
            <span class="camera-badge ${camera.isMapCamera ? 'map' : 'gameplay'}">
              ${camera.isMapCamera ? 'MAP' : 'GAME'}
            </span>
          </div>
        </div>
        
        <div class="camera-info">${camera.type}</div>

        <div class="camera-position">
          <span class="coord-label">Pos:</span>
          <span>${camera.position.x.toFixed(2)}, ${camera.position.y.toFixed(2)}, ${camera.position.z.toFixed(2)}</span>
          ${camera.target ? html`
            <span class="coord-label">Tgt:</span>
            <span>${camera.target.x.toFixed(2)}, ${camera.target.y.toFixed(2)}, ${camera.target.z.toFixed(2)}</span>
          ` : ''}
        </div>

        <div class="controls">
          <button @click=${() => this.jumpToCamera(camera.name)}>
            📷 Jump to View
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'camera-panel': CameraPanel;
  }
}
